import { AxiosError } from "axios";
import { useSetAtom } from "jotai";
import { FormEvent, useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Button, SubmitButton } from "@/components/Interface/Button/Button";
import { ProfileCard } from "@/components/Layout/BaseLayouts/ProfileCard/ProfileCard";
import { OrSeparator } from "@/components/Layout/OrSeparator";
import {
  stepDescriptionClassName,
  stepHeadingClassName,
} from "@/components/Welcome/AuthenticatorSetup/authenticator.utils";
import { AuthenticatorPhoneNumberField } from "@/components/Welcome/AuthenticatorSetup/AuthenticatorPhoneNumberField";
import { useAlert } from "@/hooks/useAlert";
import { NumberForm } from "@/hooks/useNumberForm";
import { useRequestTwoFactorPhoneNumber } from "@/hooks/useRequestTwoFactorPhoneNumber";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { useChangeTwoFactorAuthenticationSMSNumber } from "@/services/auth";
import { ChangeTwoFactorAuthenticationMethod } from "@/services/model";
import { AlertTypes, phoneNumberAtom } from "@/utils/atoms";

import { TwoFactorSmsAlternatives } from "./TwoFactorSmsAlternatives";

const nextStepByMethod: Partial<
  Record<ChangeTwoFactorAuthenticationMethod, string>
> = {
  [ChangeTwoFactorAuthenticationMethod.APP]:
    portalRoutes.user.security.setup2faApp,
  [ChangeTwoFactorAuthenticationMethod.STANDARD]:
    portalRoutes.user.security.confirmStandard,
};

/**
 * 2FA settings card for SMS users: change the 2FA phone number, or switch to
 * another 2FA method. Both are alternatives, so filling one clears the other.
 */
export const TwoFactorSmsPhonenumberChange = () => {
  const navigate = useNavigate();
  const showToast = useAlert();
  const { t } = useTranslation();
  const {
    control,
    setValue,
    setError,
    clearErrors,
    register,
    formState: { errors },
    handleSubmit,
  } = useForm<NumberForm>({
    defaultValues: {
      number: "",
    },
    mode: "onChange",
  });
  const newPhoneNumber = useWatch({ control, name: "number" });
  const [method, setMethod] =
    useState<ChangeTwoFactorAuthenticationMethod | null>(null);

  useEffect(() => {
    if (newPhoneNumber) {
      setMethod(null);
    }
  }, [newPhoneNumber]);

  const setPhoneNumber = useSetAtom(phoneNumberAtom);
  const { mutateAsync: changePhoneNumber } =
    useChangeTwoFactorAuthenticationSMSNumber();

  const phoneNumber = useRequestTwoFactorPhoneNumber();

  const handlePhonenumerChangeRejection = (error: AxiosError<string>) => {
    // If we get a 403 error, we know that the phone number is invalid
    if (error.response?.data.includes("invalid phone number")) {
      setError("number", {
        type: "manual",
        message: t("translation:label.validation.phoneBackend"),
      });
    } else {
      showToast({
        text: t("portal:user-account.security.2fa.changePhonenumber.error"),
        type: AlertTypes.error,
      });
    }
  };

  const onSubmitPhoneNumber = (data: NumberForm) => {
    changePhoneNumber({
      params: {
        phoneNumber: data.number,
      },
    })
      .then(() => {
        setPhoneNumber(data.number);
        navigate(portalRoutes.user.security.verify2faNumberChange);
      })
      .catch(handlePhonenumerChangeRejection);
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    const nextStep = method && nextStepByMethod[method];

    if (nextStep) {
      event.preventDefault();
      navigate(nextStep);

      return;
    }

    handleSubmit(onSubmitPhoneNumber)(event);
  };

  const selectMethod = (selected: ChangeTwoFactorAuthenticationMethod) => {
    setMethod(selected);
    setValue("number", "");
    clearErrors("number");
  };

  return (
    <form onSubmit={onSubmit}>
      <ProfileCard
        maxWidth="none"
        title={t("portal:user-account.security.2fa.initialSetup.title")}
        actions={
          <>
            <Button
              variant="secondary"
              size="sm"
              type="button"
              onClick={() => navigate(portalRoutes.user.security.base)}
            >
              {t("portal:user-account.security.buttons.back")}
            </Button>
            <SubmitButton
              fullWidth={false}
              size="sm"
              disabled={!newPhoneNumber && !method}
              label={t("portal:authenticator.sms.verification.finishSetup")}
            />
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <h2 className={stepHeadingClassName}>
            {t("portal:user-account.security.2fa.changePhonenumber.title")}
          </h2>
          <p className={stepDescriptionClassName}>
            <Trans
              t={t}
              i18nKey="portal:user-account.security.2fa.changePhonenumber.text"
              values={{
                phoneNumber: phoneNumber?.replace(/^00/, "+"),
              }}
              components={[<strong className="font-semibold" key="0" />]}
            />
          </p>
          <AuthenticatorPhoneNumberField register={register} errors={errors} />
        </div>
        <OrSeparator />
        <TwoFactorSmsAlternatives value={method} onChange={selectMethod} />
      </ProfileCard>
    </form>
  );
};
